import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';

import {generateLogicNodeIdentifier} from '../flowchartIdentifierUtils';
import {
  ProcessFlowchartNodeSection,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {NODE_TYPE_TO_NAME, NODE_TYPE_TO_SECTION} from '../processFlowchartConfigEditorRegistry';
import {NODE_TYPE_TO_RENDERER} from '../processFlowchartConfigEditorRegistry';
import {
  createEndNodeStaticData,
  createStaticNodeDataFromRouterConfig,
  FlowchartOptions,
} from '../processFlowchartConfigEditorUtils';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DEFAULT_NODE_PROPS, DraggableNode, NODE_TYPE_TO_CREATE_NODE_FUNCTION} from './sidepanelNodeUtils';

function DraggableLogicNode(props: {
  nodeType: ProcessFlowchartNodeType;
  addNodeToCanvas: (node: Node<unknown>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  const getDefaultName = useCallback((nodeType: ProcessFlowchartNodeType): string => {
    if (nodeType === ProcessFlowchartNodeType.ROUTER_NODE) {
      return 'Router';
    } else if (nodeType === ProcessFlowchartNodeType.STOP_NODE) {
      return 'Stop';
    }
    assert(false, `Unsupported node type: ${nodeType}`);
  }, []);

  const staticNodeData = useMemo(() => {
    if (props.nodeType === ProcessFlowchartNodeType.ROUTER_NODE) {
      return createStaticNodeDataFromRouterConfig(generateLogicNodeIdentifier(props.nodeType), {
        clientId: uuidv4(),
        name: 'Router',
        defaultFunctionSpec: null,
        functionSpecs: [],
      });
    } else if (props.nodeType === ProcessFlowchartNodeType.STOP_NODE) {
      return createEndNodeStaticData(generateLogicNodeIdentifier(props.nodeType), 'Stop');
    }
    assert(false, `Unsupported node type: ${props.nodeType}`);
  }, [props.nodeType]);

  const addNode = useCallback(
    (position: XYPosition) => {
      const nodeCreatorFn = NODE_TYPE_TO_CREATE_NODE_FUNCTION[staticNodeData.nodeType] as (
        staticData: typeof staticNodeData,
        position: XYPosition,
        options: FlowchartOptions
      ) => FlowchartNode;
      const newNode = nodeCreatorFn(staticNodeData, position, props.options);
      props.addNodeToCanvas(newNode);
    },
    [props, staticNodeData]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) =>
      NODE_TYPE_TO_RENDERER[props.nodeType]({
        ...DEFAULT_NODE_PROPS,
        id: `${props.nodeType}-sidepanel-node}`,
        type: props.nodeType,
        data: {
          ...staticNodeData,
          nodeName: getDefaultName(props.nodeType),
          sections: [],
          hideSectionSummaries: true,
          rowRenderer: () => null,
        },
        dragging,
      }),
    [props.nodeType, staticNodeData, getDefaultName]
  );

  return <DraggableNode addNode={addNode} nodeRenderer={nodeRenderer} />;
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function LogicNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section'>
): JSX.Element {
  const logicNodeTypes = props.nodeTypes.filter(
    (nodeType) => NODE_TYPE_TO_SECTION[nodeType] === ProcessFlowchartNodeSection.LOGIC
  );
  return (
    <div className={css(styles.logicNodesContainer)}>
      {logicNodeTypes
        .filter((nodeType) => {
          if (props.searchQuery == null) {
            return true;
          }
          return NODE_TYPE_TO_NAME[nodeType].toLowerCase().includes(props.searchQuery.toLowerCase());
        })
        .map((nodeType) => (
          <DraggableLogicNode
            key={nodeType}
            nodeType={nodeType}
            addNodeToCanvas={props.addNodeToCanvas}
            options={props.options}
          />
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  logicNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
});
